import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('App crashed:', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="mx-auto max-w-2xl px-6 py-16 font-sans text-t1">
        <h1 className="mb-2 font-display text-xl font-bold text-accent-red">
          Something broke while rendering.
        </h1>
        <p className="mb-4 text-sm text-t2">
          The app threw an error before it could mount. Open DevTools → Console for the full stack
          trace, then fix and reload.
        </p>
        <pre className="overflow-auto rounded-md border border-line bg-surface-2 p-3 font-mono text-xs text-t2">
          {error.message}
          {error.stack ? `\n\n${error.stack}` : null}
        </pre>
      </div>
    );
  }
}
